import React, { useCallback } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { FaHeart, FaSortAmountDown, FaSortAmountUpAlt } from "react-icons/fa";
import { MdMusicNote, MdPerson, MdFolder, MdClear } from "react-icons/md";
import { IoCalendarOutline } from "react-icons/io5";
import RefreshButton from "./Common/RefreshButton";

import * as Module from "../../modules";

const FilterContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.05);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const FilterRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const FilterLabel = styled.span`
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.6);
    min-width: 40px;
`;

const ChipGroup = styled.div`
    display: flex;
    flex-direction: row;
    gap: 6px;
    flex-wrap: wrap;
    flex: 1;
`;

interface ChipProps {
    $active?: boolean;
    $variant?: 'default' | 'favorite' | 'sort';
}

const Chip = styled.button<ChipProps>`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border-radius: 16px;
    border: 1px solid ${props => props.$active ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.3)'};
    background: ${props => props.$active ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
    color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.15);
        border-color: rgba(255, 255, 255, 0.5);
    }

    &:active {
        transform: scale(0.95);
    }

    svg {
        font-size: 0.8rem;
    }

    ${props => props.$variant === 'favorite' && props.$active && `
        background: rgba(255, 68, 68, 0.3);
        border-color: #ff4444;
        color: #ff8888;
    `}

    ${props => props.$variant === 'sort' && `
        padding: 6px 10px;
    `}
`;

const ActiveFilters = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActiveFilterTag = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.9);

    svg {
        cursor: pointer;
        font-size: 0.7rem;
        &:hover {
            color: #ff8888;
        }
    }
`;

const ClearAllButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.7rem;
    cursor: pointer;
    margin-left: auto;

    &:hover {
        color: rgba(255, 255, 255, 0.8);
    }

    svg {
        font-size: 0.7rem;
    }
`;

interface FilterChipsProps {
    onSearch: (type: string, query: string, page?: number, viewCount?: number) => void;
    onToggleCalendar: () => void;
    showFavoritesOnly: boolean;
    onToggleFavorites: () => void;
    sortOrder: 'asc' | 'desc';
    onToggleSortOrder: () => void;
}

const FilterChips = ({
    onSearch,
    onToggleCalendar,
    showFavoritesOnly,
    onToggleFavorites,
    sortOrder,
    onToggleSortOrder,
}: FilterChipsProps) => {
    const dispatch = useDispatch();
    const { searchType, searchQuery } = useSelector((state: Module.RootState) => ({
        searchType: state.query.type,
        searchQuery: state.query.query,
    }), shallowEqual);

    const searchTypes = [
        { key: 'title', label: '제목', icon: <MdMusicNote /> },
        { key: 'artist', label: '아티스트', icon: <MdPerson /> },
        { key: 'folder', label: '폴더', icon: <MdFolder /> },
    ];

    const handleTypeChange = useCallback((type: string) => {
        if (searchQuery && searchQuery !== '') {
            onSearch(type, searchQuery, 1);
        } else {
            dispatch(Module.setSearchQuery({ type, query: searchQuery }));
        }
    }, [searchQuery, onSearch, dispatch]);

    const clearFilters = useCallback(() => {
        dispatch(Module.initQuery());
        dispatch(Module.initSongs());
        if (showFavoritesOnly) {
            onToggleFavorites();
        }
    }, [dispatch, showFavoritesOnly, onToggleFavorites]);

    const hasActiveFilters = searchQuery && searchQuery !== '';

    return (
        <FilterContainer>
            <FilterRow>
                <FilterLabel>검색</FilterLabel>
                <ChipGroup>
                    {searchTypes.map((type) => (
                        <Chip
                            key={type.key}
                            $active={searchType === type.key}
                            onClick={() => handleTypeChange(type.key)}
                        >
                            {type.icon}
                            {type.label}
                        </Chip>
                    ))}
                    <Chip onClick={onToggleCalendar}>
                        <IoCalendarOutline />
                        날짜
                    </Chip>
                </ChipGroup>
            </FilterRow>

            <FilterRow>
                <FilterLabel>필터</FilterLabel>
                <ChipGroup>
                    <Chip
                        $active={showFavoritesOnly}
                        $variant="favorite"
                        onClick={onToggleFavorites}
                    >
                        <FaHeart />
                        즐겨찾기
                    </Chip>
                    <Chip
                        $variant="sort"
                        onClick={onToggleSortOrder}
                    >
                        {sortOrder === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUpAlt />}
                        {sortOrder === 'desc' ? '최신순' : '오래된순'}
                    </Chip>
                </ChipGroup>
                <RefreshButton />
            </FilterRow>

            {hasActiveFilters && (
                <ActiveFilters>
                    <FilterLabel>활성</FilterLabel>
                    <ActiveFilterTag>
                        {searchType === 'title' && '제목'}
                        {searchType === 'artist' && '아티스트'}
                        {searchType === 'folder' && '폴더'}
                        : {searchQuery}
                        <MdClear onClick={clearFilters} />
                    </ActiveFilterTag>
                    {showFavoritesOnly && (
                        <ActiveFilterTag>
                            즐겨찾기만
                            <MdClear onClick={onToggleFavorites} />
                        </ActiveFilterTag>
                    )}
                    <ClearAllButton onClick={clearFilters}>
                        <MdClear />
                        전체 해제
                    </ClearAllButton>
                </ActiveFilters>
            )}
        </FilterContainer>
    );
};

export default FilterChips;
